import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {formatDateForDisplay, getEndOfMonth, getStartOfMonth, subtractMonths} from '../../utils/dateUtils';
import styles from './dateRangePickerWithPreset.module.css';

export const DateRangePresetsType = {
  CurrentMonth: 'CurrentMonth',
  LastMonth: 'LastMonth',
  Last3Months: 'Last3Months',
  Last12Months: 'Last12Months',
  Custom: 'Custom'
}

const DateRangePicker = ({ value, onValueChange, placeholder, className = "", locale = "de-CH" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const today = new Date();
  const { t } = useTranslation();

  const presets = [
    {
      text: t("DatePickerPreset.CurrentMonth"),
      value: {
        from: getStartOfMonth(today),
        to: getEndOfMonth(today),
        preset: DateRangePresetsType.CurrentMonth
      }
    },
    {
      text: t("DatePickerPreset.LastMonth"),
      value: {
        from: getStartOfMonth(subtractMonths(today, 1)),
        to: getEndOfMonth(subtractMonths(today, 1)),
        preset: DateRangePresetsType.LastMonth
      }
    },
    {
      text: t("DatePickerPreset.Last3Months"),
      value: {
        from: getStartOfMonth(subtractMonths(today, 2)),
        to: getEndOfMonth(today),
        preset: DateRangePresetsType.Last3Months
      }
    },
    {
      text: t("DatePickerPreset.Last12Months"),
      value: {
        from: getStartOfMonth(subtractMonths(today, 11)),
        to: getEndOfMonth(today),
        preset: DateRangePresetsType.Last12Months
      }
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handlePresetClick = (preset) => {
    onValueChange(preset.value);
    setIsOpen(false);
  };

  return (
    <div className={`${styles.container} ${className}`} ref={dropdownRef}>
      <div
        className={`${styles.input} ${isOpen ? styles.inputOpen : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.inputText}>
          {value?.from && value?.to
            ? `${formatDateForDisplay(value.from, locale)} - ${formatDateForDisplay(value.to, locale)}`
            : placeholder
          }
        </span>
        <svg
          className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {presets.map((preset, index) => (
            <div
              key={index}
              onClick={() => handlePresetClick(preset)}
              className={styles.option}
            >
              {preset.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;